import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { X, Check, Loader2, ChevronLeft } from 'lucide-react';
import { toast } from '@clareefai/ui';
import { useCreateSpecificRewardCommitmentMutation } from '../../store/vendorApi';
import { useCreatePromoterMutation } from '../../store/promoterApi';
import HasJoinedStep from '../../components/rewards/specific/HasJoinedStep';
import EmailSearchStep from '../../components/rewards/specific/EmailSearchStep';
import ConfigureRewardStep from '../../components/rewards/specific/ConfigureRewardStep';
import PreviewStep from '../../components/rewards/specific/PreviewStep';
import SuccessStep from '../../components/rewards/specific/SuccessStep';
import type { CommitmentTaskType, RewardMethodType, CheckPromoterEmailResponse } from '@clareefai/types';

interface TaskSelection {
  type: CommitmentTaskType;
  requiredCount: number;
}

const AddSpecificReward: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const STEPS = [
    t('rewards.specific.steps.hasJoined'),
    t('rewards.specific.steps.findContact'),
    t('rewards.specific.steps.configure'),
    t('rewards.specific.steps.preview'),
    t('rewards.specific.steps.done'),
  ];
  const [createCommitment, { isLoading: isSubmitting }] = useCreateSpecificRewardCommitmentMutation();
  const [createPromoter, { isLoading: isCreatingPromoter }] = useCreatePromoterMutation();

  // Wizard state
  const [currentStep, setCurrentStep] = useState(0);
  const [hasJoined, setHasJoined] = useState<boolean | null>(null);
  const [showInlineCreate, setShowInlineCreate] = useState(false);

  // Inline create promoter fields
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newCompany, setNewCompany] = useState('');

  // Promoter data
  const [promoterId, setPromoterId] = useState<string | null>(null);
  const [promoterEmail, setPromoterEmail] = useState('');
  const [promoterName, setPromoterName] = useState<string | null>(null);

  // Configure step data
  const [rewardMethod, setRewardMethod] = useState<RewardMethodType>('DISCOUNT');
  const [giftCardAmount, setGiftCardAmount] = useState(0);
  const [giftCardCurrency, setGiftCardCurrency] = useState('EUR');
  const [discountUnit, setDiscountUnit] = useState('percentage');
  const [discountAmount, setDiscountAmount] = useState(0);
  const [additionalNote, setAdditionalNote] = useState('');
  const [selectedTasks, setSelectedTasks] = useState<TaskSelection[]>([]);
  const [deadline, setDeadline] = useState('');
  const [invitationMessage, setInvitationMessage] = useState('');

  const handleClose = () => navigate('/rewards');

  const handleHasJoinedContinue = () => {
    if (hasJoined === true) {
      setCurrentStep(1); // Email search
    } else if (hasJoined === false) {
      setShowInlineCreate(true); // Show inline create form
    }
  };

  const handleInlineCreate = async () => {
    if (!newFirstName.trim() || !newLastName.trim() || !newEmail.trim()) return;

    try {
      const result = await createPromoter({
        first_name: newFirstName.trim(),
        last_name: newLastName.trim(),
        email: newEmail.trim(),
        company: newCompany.trim() || undefined,
      }).unwrap();

      toast.success(t('rewards.specific.promoterCreated'));
      setPromoterId(result.id);
      setPromoterEmail(newEmail.trim());
      setPromoterName(`${newFirstName.trim()} ${newLastName.trim()}`);
      setShowInlineCreate(false);
      setCurrentStep(2); // Go directly to Configure step
    } catch (err: any) {
      toast.error(err?.data?.message || t('rewards.specific.failedToCreatePromoter'));
    }
  };

  const handlePromoterFound = (data: CheckPromoterEmailResponse) => {
    setPromoterId(data.promoter_id);
    setPromoterEmail(data.promoter_email || '');
    setPromoterName(data.promoter_name);
    setCurrentStep(2); // Configure
  };

  const handleTaskToggle = (type: CommitmentTaskType) => {
    setSelectedTasks((prev) => {
      const exists = prev.find((t) => t.type === type);
      if (exists) return prev.filter((t) => t.type !== type);
      return [...prev, { type, requiredCount: 1 }];
    });
  };

  const handleTaskCountChange = (type: CommitmentTaskType, count: number) => {
    setSelectedTasks((prev) =>
      prev.map((t) => (t.type === type ? { ...t, requiredCount: count } : t))
    );
  };

  const handleSubmit = async () => {
    if (!promoterId || !promoterEmail) {
      toast.error(t('rewards.specific.missingPromoterInfo'));
      return;
    }

    if (selectedTasks.length === 0) {
      toast.error(t('rewards.specific.selectAtLeastOneTask'));
      return;
    }

    try {
      await createCommitment({
        promoter_id: promoterId,
        email: promoterEmail,
        tasks: selectedTasks.map((t) => ({ type: t.type, required_count: t.requiredCount })),
        reward_method: rewardMethod,
        gift_card_amount: (rewardMethod === 'GIFT_CARD' || rewardMethod === 'BOTH') ? giftCardAmount : undefined,
        gift_card_currency: (rewardMethod === 'GIFT_CARD' || rewardMethod === 'BOTH') ? giftCardCurrency : undefined,
        discount_unit: (rewardMethod === 'DISCOUNT' || rewardMethod === 'BOTH') ? discountUnit : undefined,
        discount_amount: (rewardMethod === 'DISCOUNT' || rewardMethod === 'BOTH') ? discountAmount : undefined,
        additional_note: additionalNote || undefined,
        deadline: new Date(deadline + 'T23:59:59Z').toISOString(),
        invitation_message: invitationMessage || undefined,
      }).unwrap();

      toast.success(t('rewards.specific.commitmentCreated'));
      setCurrentStep(4); // Success
    } catch (err: any) {
      toast.error(err?.data?.message || t('rewards.specific.failedToCreate'));
    }
  };

  const handleSendAnother = () => {
    setCurrentStep(0);
    setHasJoined(null);
    setShowInlineCreate(false);
    setNewFirstName('');
    setNewLastName('');
    setNewEmail('');
    setNewCompany('');
    setPromoterId(null);
    setPromoterEmail('');
    setPromoterName(null);
    setRewardMethod('DISCOUNT');
    setGiftCardAmount(0);
    setGiftCardCurrency('EUR');
    setDiscountUnit('percentage');
    setDiscountAmount(0);
    setAdditionalNote('');
    setSelectedTasks([]);
    setDeadline('');
    setInvitationMessage('');
  };

  const activeStepIndex = Math.min(currentStep, 4);

  const isValidNewPromoter = newFirstName.trim().length >= 2
    && newLastName.trim().length >= 2
    && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-xl shadow-sm">
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gradient-to-r from-[#4339A0] to-indigo-500 text-white rounded-t-xl">
            <h2 className="text-lg font-semibold">{t('rewards.specific.addTitle')}</h2>
            <button onClick={handleClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Step Indicator */}
          {currentStep < 4 && (
            <div className="flex items-center justify-center gap-1 p-6 pb-0">
              {STEPS.slice(0, 4).map((label, i) => (
                <React.Fragment key={label}>
                  <div className="flex flex-col items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold border-2 transition-colors ${
                      i < activeStepIndex ? 'bg-[#4339A0] border-[#4339A0] text-white' :
                      i === activeStepIndex ? 'border-[#4339A0] text-[#4339A0]' :
                      'border-gray-300 text-gray-400'
                    }`}>
                      {i < activeStepIndex ? <Check className="w-4 h-4" /> : i + 1}
                    </div>
                    <span className={`text-[10px] mt-1 ${i <= activeStepIndex ? 'text-[#4339A0]' : 'text-gray-400'}`}>{label}</span>
                  </div>
                  {i < 3 && <div className={`w-12 h-0.5 mb-4 ${i < activeStepIndex ? 'bg-[#4339A0]' : 'bg-gray-300'}`} />}
                </React.Fragment>
              ))}
            </div>
          )}

          {/* Content */}
          <div className="p-6">
            {currentStep === 0 && !showInlineCreate && (
              <HasJoinedStep
                hasJoined={hasJoined}
                onSelect={setHasJoined}
                onCancel={handleClose}
                onContinue={handleHasJoinedContinue}
              />
            )}
            {currentStep === 0 && showInlineCreate && (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('rewards.specific.inviteNew')}</h3>
                  <p className="text-gray-500">{t('rewards.specific.inviteNewDesc')}</p>
                </div>

                <div className="max-w-md mx-auto space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('rewards.specific.firstName')} *</label>
                      <input
                        type="text"
                        value={newFirstName}
                        onChange={(e) => setNewFirstName(e.target.value)}
                        placeholder="John"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4339A0] focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('rewards.specific.lastName')} *</label>
                      <input
                        type="text"
                        value={newLastName}
                        onChange={(e) => setNewLastName(e.target.value)}
                        placeholder="Doe"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4339A0] focus:border-transparent text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('rewards.specific.email')} *</label>
                    <input
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="john@company.com"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4339A0] focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('rewards.specific.company')}</label>
                    <input
                      type="text"
                      value={newCompany}
                      onChange={(e) => setNewCompany(e.target.value)}
                      placeholder={t('rewards.specific.companyPlaceholder')}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4339A0] focus:border-transparent text-sm"
                    />
                  </div>

                  <button
                    onClick={handleInlineCreate}
                    disabled={!isValidNewPromoter || isCreatingPromoter}
                    className="w-full py-2.5 bg-[#4339A0] text-white rounded-lg hover:bg-[#3d2f8f] transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isCreatingPromoter ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    {isCreatingPromoter ? t('rewards.specific.creating') : t('rewards.specific.create')}
                  </button>
                </div>

                <div className="flex justify-between pt-4">
                  <button
                    onClick={() => setShowInlineCreate(false)}
                    className="flex items-center px-6 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" /> {t('rewards.specific.back')}
                  </button>
                  <button onClick={handleClose} className="px-6 py-2.5 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors">
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            )}
            {currentStep === 1 && (
              <EmailSearchStep
                onPromoterFound={handlePromoterFound}
                onBack={() => setCurrentStep(0)}
                onCancel={handleClose}
              />
            )}
            {currentStep === 2 && (
              <ConfigureRewardStep
                email={promoterEmail}
                rewardMethod={rewardMethod}
                giftCardAmount={giftCardAmount}
                giftCardCurrency={giftCardCurrency}
                discountUnit={discountUnit}
                discountAmount={discountAmount}
                additionalNote={additionalNote}
                selectedTasks={selectedTasks}
                deadline={deadline}
                onRewardMethodChange={setRewardMethod}
                onGiftCardAmountChange={setGiftCardAmount}
                onGiftCardCurrencyChange={setGiftCardCurrency}
                onDiscountUnitChange={setDiscountUnit}
                onDiscountAmountChange={setDiscountAmount}
                onAdditionalNoteChange={setAdditionalNote}
                onTaskToggle={handleTaskToggle}
                onTaskCountChange={handleTaskCountChange}
                onDeadlineChange={setDeadline}
                onBack={() => setCurrentStep(hasJoined ? 1 : 0)}
                onCancel={handleClose}
                onNext={() => setCurrentStep(3)}
              />
            )}
            {currentStep === 3 && (
              <PreviewStep
                email={promoterEmail}
                promoterName={promoterName}
                rewardMethod={rewardMethod}
                giftCardAmount={giftCardAmount}
                giftCardCurrency={giftCardCurrency}
                discountUnit={discountUnit}
                discountAmount={discountAmount}
                additionalNote={additionalNote}
                selectedTasks={selectedTasks}
                deadline={deadline}
                invitationMessage={invitationMessage}
                onInvitationMessageChange={setInvitationMessage}
                onBack={() => setCurrentStep(2)}
                onCancel={handleClose}
                onSubmit={handleSubmit}
                isSubmitting={isSubmitting}
              />
            )}
            {currentStep === 4 && (
              <SuccessStep
                email={promoterEmail}
                rewardMethod={rewardMethod}
                giftCardAmount={giftCardAmount}
                giftCardCurrency={giftCardCurrency}
                discountUnit={discountUnit}
                discountAmount={discountAmount}
                selectedTasks={selectedTasks}
                onSendAnother={handleSendAnother}
                onDone={handleClose}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddSpecificReward;
